import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { FaChevronDown, FaChevronUp, FaArrowLeft, FaPhone, FaHardHat, FaImages, FaUsers } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import CTASection from '../ui/CTASection';
import Footer from '../layout/Footer';
import './FAQ.css';

const FAQPage = () => {
  const navigate = useNavigate();
  const titleRef = useScrollAnimation();
  const [openItems, setOpenItems] = useState({});

  const handleContactClick = (e) => {
    e.preventDefault();
    window.location.href = 'tel:+13136316020';
  };

  const toggleItem = (index) => {
    setOpenItems(prev => {
      // If the clicked item is already open, close it
      if (prev[index]) {
        return { [index]: false };
      }
      // Otherwise, close all items and open only the clicked one
      return { [index]: true };
    });
  };

  const faqData = [
    {
      question: "How long does a typical remodeling project take?",
      answer: "The timeline varies depending on the scope of the project:\n\n• Small Projects (e.g., bathrooms): 2–4 weeks.\n• Medium Projects (e.g., kitchens): 6–8 weeks.\n• Large Projects (e.g., whole-home remodels): 3–6 months or more.\n\nWe provide a detailed timeline during the planning phase to ensure transparency."
    },
    {
      question: "Do I need to move out during the remodeling process?",
      answer: "It depends on the scale of the work:\n\n• For smaller projects, staying at home is usually fine.\n• For major renovations affecting key areas like the kitchen or bathrooms, temporary relocation might be more convenient.\n\nWe work with you to minimize disruption and keep your home livable whenever possible."
    },
    {
      question: "What permits are required for a remodeling project?",
      answer: "Most remodeling projects require permits, particularly for:\n\n• Structural changes.\n• Electrical or plumbing work.\n• Additions or exterior modifications.\n\nWe handle the permit process to ensure your project complies with local building codes."
    },
    {
      question: "How much does a remodeling project cost?",
      answer: "Costs vary widely based on the project's size, materials, and complexity. For example:\n\n• Bathroom Remodels: $10,000–$30,000.\n• Kitchen Remodels: $25,000–$75,000.\n• Room Additions: $50,000 and up.\n\nWe provide detailed, itemized estimates during the consultation phase to fit your budget."
    },
    {
      question: "Can I use my own materials or fixtures?",
      answer: "Absolutely! If you have specific materials or fixtures in mind, we can incorporate them into the design. However, we may recommend quality checks to ensure they meet project standards."
    },
    {
      question: "Do you offer financing options?",
      answer: "Yes, we work with trusted financing partners to provide options that make remodeling affordable. During your consultation, we'll discuss available plans, including low-interest or deferred-payment options."
    },
    {
      question: "How do I prepare my home for remodeling?",
      answer: "Here are some tips:\n\n• Declutter the area and remove valuable or fragile items.\n• Set up temporary spaces, such as a kitchen in another room if remodeling the main kitchen.\n• Make arrangements for pets and children to ensure their safety during construction.\n\nWe provide a detailed checklist before the work begins to help you prepare."
    },
    {
      question: "Do you offer warranties on your work?",
      answer: "Yes, we stand by our craftsmanship with comprehensive warranties:\n\n• Labor Warranty: Covers any workmanship issues for one year.\n• Manufacturer Warranties: Apply to materials and fixtures we source for your project."
    },
    {
      question: "Can I make changes once the project has started?",
      answer: "Yes, but changes during the construction phase may affect the timeline and budget. We'll discuss options and adjust plans transparently to ensure you're satisfied."
    },
    {
      question: "What makes RC Remodel different from other remodelers?",
      answer: "• Experience: Years of industry expertise with a proven track record.\n• Transparency: Clear communication, detailed estimates, and no hidden costs.\n• Quality: Premium materials and meticulous attention to detail.\n• Customer Focus: We prioritize your vision, timeline, and budget."
    }
  ];

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main>
      <Helmet>
        <title>RC Remodel - FAQs</title>
        <meta name="description" content="Get answers to common questions about RC Remodel's home remodeling services, costs, timelines, and process. Expert answers to help plan your renovation project." />
        <meta name="keywords" content="home remodeling FAQ, remodeling questions, renovation cost, remodeling timeline, contractor questions, home improvement FAQ" />
        <link rel="canonical" href="https://rcremodel.com/faq" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Frequently Asked Questions | RC Remodel" />
        <meta property="og:description" content="Get answers to common questions about RC Remodel's home remodeling services, costs, timelines, and process." />
        <meta property="og:url" content="https://rcremodel.com/faq" />
        <meta property="og:image" content="https://rcremodel.com/images/logos/RC Remodel Yellow BG.jpg" />
        
        {/* Twitter */}
        <meta property="twitter:title" content="Frequently Asked Questions | RC Remodel" />
        <meta property="twitter:description" content="Get answers to common questions about RC Remodel's home remodeling services, costs, timelines, and process." />
        <meta property="twitter:image" content="https://rcremodel.com/images/logos/RC Remodel Yellow BG.jpg" />
      </Helmet>
      
      <div className="faq-page">
        <div className="page-header">
          <div className="container">
            <div className="page-nav">
              <button 
                className="back-btn"
                onClick={() => navigate('/')}
              >
                <FaArrowLeft className="back-icon" />
                Back to Home
              </button>
              <div className="page-nav-right">
                <button className="nav-action-btn" onClick={handleContactClick} title="Call us">
                  <FaPhone className="nav-icon" />
                  <span className="nav-text">Call us</span>
                </button>
                <button className="nav-action-btn" onClick={() => navigate('/all-services')} title="Our Services">
                  <FaHardHat className="nav-icon" />
                  <span className="nav-text">Our Services</span>
                </button>
                <button className="nav-action-btn" onClick={() => navigate('/gc-services')} title="General Contractor">
                  <FaUsers className="nav-icon" />
                  <span className="nav-text">General Contractor</span>
                </button>
                <button className="nav-action-btn" onClick={() => navigate('/gallery')} title="Our Gallery">
                  <FaImages className="nav-icon" />
                  <span className="nav-text">Our Gallery</span>
                </button>
              </div>
            </div>
            <h1 ref={titleRef} className="page-title">Frequently Asked Questions</h1>
            <p className="page-subtitle pad-h-xs-6">
              Get answers to common questions about our home remodeling services and process.
            </p>
          </div>
        </div>

        <div className="container">
          <section className="section faq">
            <div className="faq-list">
              {faqData.map((item, index) => (
                <div key={index} className={`faq-item ${openItems[index] ? 'open' : ''}`}>
                  <div 
                    className="faq-question"
                    onClick={() => toggleItem(index)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleItem(index);
                      }
                    }}
                  >
                    <h2>{item.question}</h2>
                    <div className="faq-icon">
                      {openItems[index] ? <FaChevronUp /> : <FaChevronDown />}
                    </div>
                  </div>
                  <div className={`faq-answer ${openItems[index] ? 'expanded' : ''}`}>
                    <div className="faq-answer-content">
                      <p>{item.answer}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* Call to Action */}
          <CTASection 
            title="Still Have Questions?"
            description="Can't find what you're looking for? Our team is here to help with any questions about your remodeling project."
            buttonText="Contact Us Today"
          />
        </div>
      </div>
      <Footer />
    </main>
  );
};

export default FAQPage;
